import React, { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/contexts/CartContext";
import { api, formatErr, formatTL } from "@/lib/api";

const METHODS = [
  { key: "havale", label: "Havale / EFT", desc: "Banka bilgilerimizi e-postanıza göndereceğiz." },
  { key: "kapida", label: "Kapıda Ödeme", desc: "Teslimat sırasında nakit veya kart ile ödeme." },
  { key: "whatsapp", label: "WhatsApp ile Onay", desc: "Sipariş detayları WhatsApp üzerinden onaylanır." },
];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const { settings } = useOutletContext() || {};
  const currency = settings?.currency_symbol || "₺";
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", address: "", city: "", notes: "", payment_method: "havale" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Sepetiniz boş"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", { ...form, items });
      setSuccess(data);
      clear();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="order-success">
        <CheckCircle2 size={56} className="mx-auto text-larc-gold" />
        <h1 className="font-heading text-5xl mt-6">Siparişiniz alındı</h1>
        <p className="text-larc-muted mt-4">Teşekkürler. Sipariş numaranız:</p>
        <div className="font-heading text-3xl text-larc-ink mt-2" data-testid="order-number">{success.order_number}</div>
        <p className="text-larc-muted mt-6 max-w-md mx-auto">Sipariş detaylarınız için sizinle en kısa sürede iletişime geçeceğiz. Bu numarayı sipariş takibi için kaydedebilirsiniz.</p>
        <Link to="/" className="mt-10 inline-flex items-center gap-2 bg-larc-gold text-white rounded-full px-8 py-3.5 text-sm hover:bg-larc-gold-hover transition-colors">
          Anasayfa <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-heading text-4xl">Sepetiniz boş</h1>
        <Link to="/urunler" className="mt-6 inline-flex items-center gap-2 text-larc-gold">Ürünleri keşfet <ArrowRight size={14} /></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="checkout-page">
      <h1 className="font-heading text-5xl md:text-6xl tracking-tight">Sipariş Bilgileri</h1>
      <p className="text-larc-muted mt-3">Bilgilerinizi tamamlayın, siparişiniz atölyeye düşsün.</p>

      <form onSubmit={submit} className="mt-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {/* Customer */}
          <section>
            <div className="text-xs uppercase tracking-[0.18em] text-larc-gold">— Müşteri Bilgileri</div>
            <div className="mt-5 grid sm:grid-cols-2 gap-5">
              <Field label="Ad Soyad" required value={form.customer_name} onChange={handle("customer_name")} testid="checkout-name" />
              <Field label="Telefon" required value={form.phone} onChange={handle("phone")} testid="checkout-phone" />
              <Field label="E-posta" type="email" value={form.email} onChange={handle("email")} testid="checkout-email" />
              <Field label="Şehir" required value={form.city} onChange={handle("city")} testid="checkout-city" />
            </div>
            <div className="mt-5">
              <Field label="Adres" required value={form.address} onChange={handle("address")} testid="checkout-address" />
            </div>
            <div className="mt-5">
              <Field label="Sipariş Notu (opsiyonel)" value={form.notes} onChange={handle("notes")} testid="checkout-notes" textarea />
            </div>
          </section>

          {/* Payment */}
          <section>
            <div className="text-xs uppercase tracking-[0.18em] text-larc-gold">— Ödeme Yöntemi</div>
            <div className="mt-5 grid gap-3">
              {METHODS.map((m) => (
                <label key={m.key} className={`flex items-start gap-4 p-5 rounded-2xl border cursor-pointer transition-colors bg-white ${form.payment_method === m.key ? "border-larc-gold" : "border-larc-border"}`}>
                  <input
                    type="radio"
                    name="pm"
                    value={m.key}
                    checked={form.payment_method === m.key}
                    onChange={() => setForm({ ...form, payment_method: m.key })}
                    className="mt-1 accent-larc-gold"
                    data-testid={`pm-${m.key}`}
                  />
                  <div>
                    <div className="font-medium">{m.label}</div>
                    <div className="text-sm text-larc-muted mt-1">{m.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </section>
        </div>

        {/* Summary */}
        <aside className="bg-white border border-larc-border rounded-2xl p-6 md:p-8 h-fit sticky top-28">
          <h3 className="font-heading text-2xl">Sipariş Özeti</h3>
          <div className="mt-5 space-y-3 max-h-64 overflow-auto pr-1">
            {items.map((it) => (
              <div key={it.product_id} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-larc-alt shrink-0">
                  {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{it.name}</div>
                  <div className="text-xs text-larc-muted">{it.quantity} × {formatTL(it.price, currency)}</div>
                </div>
                <div className="text-sm">{formatTL(it.price * it.quantity, currency)}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 border-t border-larc-border pt-5 flex items-center justify-between">
            <span className="font-medium">Toplam</span>
            <span className="font-heading text-2xl">{formatTL(subtotal, currency)}</span>
          </div>
          <button type="submit" disabled={loading} className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-larc-ink text-white rounded-full px-6 py-3.5 text-sm hover:bg-larc-gold transition-colors disabled:opacity-50 active:scale-95" data-testid="submit-order">
            {loading ? "Gönderiliyor…" : "Siparişi Onayla"} <ArrowRight size={16} />
          </button>
          <p className="text-xs text-larc-muted mt-4 text-center">Onayınızla siparişiniz atölyemize iletilir.</p>
        </aside>
      </form>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testid, textarea }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-larc-muted">{label}{required && " *"}</span>
      {textarea ? (
        <textarea required={required} value={value} onChange={onChange} rows={3} className="mt-2 w-full bg-white border border-larc-border rounded-lg px-4 py-3 outline-none focus:border-larc-gold text-sm" data-testid={testid} />
      ) : (
        <input required={required} type={type} value={value} onChange={onChange} className="mt-2 w-full bg-white border border-larc-border rounded-lg px-4 py-3 outline-none focus:border-larc-gold text-sm" data-testid={testid} />
      )}
    </label>
  );
}
